import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowDownTrayIcon,
    DocumentTextIcon,
} from "@heroicons/react/24/outline";
import api from "../../api";
import SelectInput from "../../components/SelectInput";
import Table from "../../components/Table";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import PatientModuleHeader from "../../components/patients/layout/PatientModuleHeader";
import PatientModuleLayout from "../../components/patients/layout/PatientModuleLayout";

const modeOptions = [
    { name: "Lista de consultas", value: 1 },
    { name: "Resumen", value: 2 },
];

const sortOptions = [
    { name: "Fecha descendente", value: 1 },
    { name: "Fecha ascendente", value: 2 },
];

const visionValues = [
    "",
    "20/10",
    "20/15",
    "20/20",
    "20/25",
    "20/30",
    "20/40",
    "20/50",
    "20/60",
    "20/70",
    "20/80",
    "20/100",
    "20/150",
    "20/200",
    "20/400",
    "CD",
    "MM",
    "SPL",
    "BPL",
    "MPL",
    "NPL",
];

const columns = [
    { name: "index", displayText: "#" },
    { name: "consultationDate", displayText: "Fecha de consulta" },
    { name: "diagnoses", displayText: "Diagnostico", truncate: true },
    { name: "doctor", displayText: "Doctor" },
    { name: "prescription", displayText: "Receta" },
];

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

function SummaryCard({ label, value, accentClassName }) {
    return (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950">
            <p className={classNames("text-sm font-semibold", accentClassName)}>
                {label}
            </p>
            <p className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">
                {value || "--"}
            </p>
        </section>
    );
}

function DetailCard({ title, children }) {
    return (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-600 dark:text-slate-300">
                {title}
            </h2>
            <div className="mt-4">{children}</div>
        </section>
    );
}

function InfoRow({ label, value }) {
    return (
        <div className="grid gap-1 sm:grid-cols-[160px_minmax(0,1fr)] sm:gap-4">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                {label}
            </p>
            <p className="text-sm text-slate-800 dark:text-slate-100">
                {value || "--"}
            </p>
        </div>
    );
}

function VisionValue({ label, value }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-900">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {label}
            </p>
            <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
                {value || "--"}
            </p>
        </div>
    );
}

function ConsultationHistoryMobileList({ consultations, idPatient }) {
    return (
        <div className="space-y-3 lg:hidden">
            {consultations.map((consultation) => (
                <Link
                    key={consultation.id}
                    to={`/patients/${idPatient}/consultations/${consultation.id}`}
                    className="block rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-primary/30 hover:shadow-md dark:border-slate-800 dark:bg-slate-950"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-primary">
                                {consultation.consultationDate || "--"}
                            </p>
                            <p className="mt-1 truncate text-sm text-slate-700 dark:text-slate-200">
                                {consultation.doctor || "Sin profesional"}
                            </p>
                        </div>
                        <span
                            className={classNames(
                                "inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold",
                                consultation.prescription === "Si"
                                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                                    : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                            )}
                        >
                            Receta {consultation.prescription || "No"}
                        </span>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3">
                        <div className="min-w-0">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                Diagnostico
                            </p>
                            <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                                {consultation.diagnoses || "--"}
                            </p>
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">
                                <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                    N°
                                </span>{" "}
                                {consultation.index}
                            </p>
                        </div>
                    </div>
                </Link>
            ))}
        </div>
    );
}

function ConsultationSummaryView({
    consultations,
    currentIndex,
    setCurrentIndex,
    sort,
    handleSort,
}) {
    const consultation = consultations[currentIndex];

    return (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
            <DetailCard title="Navegacion">
                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                Consulta actual
                            </p>
                            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
                                {consultations.length > 0
                                    ? `Consulta ${currentIndex + 1} de ${consultations.length}`
                                    : "Sin consultas"}
                            </p>
                        </div>
                        <DocumentTextIcon className="h-5 w-5 text-slate-400" />
                    </div>

                    <SelectInput
                        handleChange={handleSort}
                        value={sort}
                        className="min-h-[44px] w-full rounded-xl border border-slate-300 bg-white px-3 pr-10 shadow-sm dark:border-slate-700 dark:bg-slate-950"
                        options={sortOptions.map((element) => (
                            <option key={element.value} value={element.value}>
                                {element.name}
                            </option>
                        ))}
                        parse={true}
                    />

                    <div className="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            onClick={() =>
                                setCurrentIndex((prev) => Math.max(0, prev - 1))
                            }
                            disabled={currentIndex === 0}
                            className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-primary/30 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                        >
                            Anterior
                        </button>
                        <button
                            type="button"
                            onClick={() =>
                                setCurrentIndex((prev) =>
                                    Math.min(consultations.length - 1, prev + 1)
                                )
                            }
                            disabled={
                                consultations.length === 0 ||
                                currentIndex === consultations.length - 1
                            }
                            className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Siguiente
                        </button>
                    </div>
                </div>
            </DetailCard>

            <div className="space-y-4">
                {consultation ? (
                    <>
                        <DetailCard title="Datos de la consulta">
                            <div className="space-y-4">
                                <InfoRow
                                    label="Fecha"
                                    value={consultation.consultationDate}
                                />
                                <InfoRow
                                    label="Doctor"
                                    value={consultation.doctor}
                                />
                                <InfoRow
                                    label="Diagnostico"
                                    value={consultation.diagnoses}
                                />
                                <InfoRow
                                    label="Motivo"
                                    value={
                                        consultation.general
                                            ?.motiveOfConsultation
                                    }
                                />
                            </div>
                        </DetailCard>

                        <DetailCard title="Vision y tension">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-3">
                                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                                        Con correccion
                                    </p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <VisionValue
                                            label="OI"
                                            value={
                                                consultation?.visionTension
                                                    ?.visionWithCorrectionLeft
                                                    ? visionValues[
                                                          consultation
                                                              .visionTension
                                                              .visionWithCorrectionLeft
                                                      ]
                                                    : "--"
                                            }
                                        />
                                        <VisionValue
                                            label="OD"
                                            value={
                                                consultation?.visionTension
                                                    ?.visionWithCorrectionRight
                                                    ? visionValues[
                                                          consultation
                                                              .visionTension
                                                              .visionWithCorrectionRight
                                                      ]
                                                    : "--"
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                                        Sin correccion
                                    </p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <VisionValue
                                            label="OI"
                                            value={
                                                consultation?.visionTension
                                                    ?.visionWithoutCorrectionLeft
                                                    ? visionValues[
                                                          consultation
                                                              .visionTension
                                                              .visionWithoutCorrectionLeft
                                                      ]
                                                    : "--"
                                            }
                                        />
                                        <VisionValue
                                            label="OD"
                                            value={
                                                consultation?.visionTension
                                                    ?.visionWithoutCorrectionRight
                                                    ? visionValues[
                                                          consultation
                                                              .visionTension
                                                              .visionWithoutCorrectionRight
                                                      ]
                                                    : "--"
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </DetailCard>
                    </>
                ) : (
                    <DetailCard title="Resumen">
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            No se han encontrado consultas.
                        </p>
                    </DetailCard>
                )}
            </div>
        </div>
    );
}

export default function ConsultationHistoryPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const [patient, setPatient] = useState();
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [mode, setMode] = useState(1);
    const [sort, setSort] = useState(1);
    const [exportingPdf, setExportingPdf] = useState(false);
    const isDesktop = useMediaQuery("(min-width: 1024px)");
    const { idPatient } = useParams();

    useEffect(() => {
        if (isDesktop) {
            setSidebarOpen(false);
        }
    }, [isDesktop]);

    const pages = [
        { name: "Pacientes", href: "/patients", current: false },
        {
            name: `${patient?.name}`,
            href: `/patients/${idPatient}`,
            current: false,
        },
        { name: "Historial de consultas", href: "#", current: true },
    ];

    const consultations = useMemo(
        () => history?.consultationList ?? [],
        [history?.consultationList]
    );

    const handleMode = (field, value) => {
        setMode(value);
    };

    const handleSort = (field, value) => {
        setSort(value);
    };

    const handleExportClinicalHistory = async () => {
        try {
            setExportingPdf(true);
            const blob = await api.patients.exportClinicalHistory(idPatient);
            const url = window.URL.createObjectURL(blob);
            window.open(url, "_blank");
        } catch (error) {
            console.error("Error al exportar historial clínico:", error);
        } finally {
            setExportingPdf(false);
        }
    };

    const handleSidebarToggle = () => {
        if (isDesktop) {
            setSidebarOpenDesktop(!sidebarOpenDesktop);
            return;
        }
        setSidebarOpen(!sidebarOpen);
    };

    useEffect(() => {
        api.patients
            .getOne(idPatient)
            .then((response) => {
                setPatient({
                    ...response,
                    name: `${response.firstName} ${response.lastName}`,
                });
            })
            .catch((error) => console.log(error));
    }, [idPatient]);

    useEffect(() => {
        setLoading(true);
        api.patients
            .getHistory(idPatient, sort === 2 ? "date" : "-date")
            .then((response) => {
                setHistory({
                    ...response,
                    consultationList: response.consultationList
                        ? response.consultationList.map((element, idx) => ({
                              ...element,
                              index: idx + 1,
                              prescription: element.prescription ? "Si" : "No",
                          }))
                        : [],
                });
            })
            .finally(() => setLoading(false));
    }, [idPatient, sort]);

    useEffect(() => {
        if (currentIndex > consultations.length - 1) {
            setCurrentIndex(Math.max(consultations.length - 1, 0));
        }
    }, [consultations.length, currentIndex]);

    return (
        <PatientModuleLayout
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            loading={loading}
            header={
                <PatientModuleHeader
                    pages={pages}
                    onToggleSidebar={handleSidebarToggle}
                    mobileBackTo={`/patients/${idPatient}`}
                    mobileBackLabel="Paciente"
                    mobileTitle={patient?.name || "Paciente"}
                />
            }
        >
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 py-4 lg:px-6 lg:py-6">
                            <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-5">
                                <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                                            Historial de consultas
                                        </p>
                                        <h1 className="mt-1 text-xl font-semibold text-slate-900 dark:text-white sm:text-2xl">
                                            {patient?.name || "Paciente"}
                                        </h1>
                                    </div>

                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:flex xl:items-center">
                                        <SelectInput
                                            value={mode}
                                            name="mode"
                                            handleChange={handleMode}
                                            className="min-h-[44px] w-full rounded-xl border border-slate-300 bg-white px-3 pr-10 shadow-sm dark:border-slate-700 dark:bg-slate-950"
                                            options={modeOptions.map((element) => (
                                                <option
                                                    key={element.value}
                                                    value={element.value}
                                                >
                                                    {element.name}
                                                </option>
                                            ))}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleExportClinicalHistory}
                                            disabled={exportingPdf}
                                            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {exportingPdf ? (
                                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                            ) : (
                                                <ArrowDownTrayIcon className="h-4 w-4" />
                                            )}
                                            Historial clinico PDF
                                        </button>
                                    </div>
                                </div>
                            </section>

                            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                                <SummaryCard
                                    label="Ultima consulta"
                                    value={history?.lastConsultationDate}
                                    accentClassName="text-rose-500"
                                />
                                <SummaryCard
                                    label="N° total de consultas"
                                    value={history?.consultationCount}
                                    accentClassName="text-sky-500"
                                />
                                <SummaryCard
                                    label="Ultima consulta con"
                                    value={history?.lastDoctor}
                                    accentClassName="text-amber-500"
                                />
                            </div>

                            {mode === 2 ? (
                                <ConsultationSummaryView
                                    consultations={consultations}
                                    currentIndex={currentIndex}
                                    setCurrentIndex={setCurrentIndex}
                                    sort={sort}
                                    handleSort={handleSort}
                                />
                            ) : consultations.length > 0 ? (
                                <>
                                    <ConsultationHistoryMobileList
                                        consultations={consultations}
                                        idPatient={idPatient}
                                    />
                                    {isDesktop && (
                                        <div className="hidden lg:block">
                                            <Table
                                                actiontext="Detalle"
                                                module={`patients/${idPatient}/consultations`}
                                                columns={columns}
                                                rows={consultations}
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <DetailCard title="Lista de consultas">
                                    <p className="text-sm text-slate-500 dark:text-slate-400">
                                        No se han encontrado consultas.
                                    </p>
                                </DetailCard>
                            )}
            </div>
        </PatientModuleLayout>
    );
}
